import type {
  Actuator,
  Body,
  BodyUUID,
  Equality,
  Quaternion,
  Vector3,
  WorldBody,
} from "../../types/scene";
import * as THREE from "three";
import * as RuntimeRegistry from "../../runtime/objectRegistry";
import { create } from "mutative";
import { generateUuid } from "../../utils/id";
import { logger } from "../../utils/logger";
import type { SceneStoreGetState, SceneStoreSetState } from "../sceneStore.types";
import type { BodyActions, BodyInput } from "./actionTypes";
import { withHistory } from "./withHistory";
import {
  generateUniqueWorldBodyName,
  type BodyRecord,
  type RuntimeRegistryMap,
  type WorldBodyRef,
} from "./bodyActionUtils";

const removeBodyFreeJoints = (
  draft: {
    bodies: Record<string, Body>;
    joints: Record<string, { kind?: string; name?: string } | undefined>;
    actuators?: Record<string, Actuator | undefined>;
    equality?: Record<string, Equality | undefined>;
  },
  bodyUuid: BodyUUID
) => {
  const body = draft.bodies[bodyUuid];
  if (!body || !Array.isArray(body.joints)) return;

  const removedFreeJoints = body.joints
    .filter((jointUuid) => draft.joints?.[jointUuid]?.kind === "free")
    .map((jointUuid) => ({
      uuid: jointUuid,
      name: draft.joints[jointUuid]?.name,
    }));
  if (removedFreeJoints.length === 0) return;

  const removedTargets = new Set<string>();
  const removedUuids = new Set<string>();
  for (const joint of removedFreeJoints) {
    removedUuids.add(joint.uuid);
    removedTargets.add(joint.uuid);
    if (joint.name) removedTargets.add(joint.name);
  }

  body.joints = body.joints.filter((jointUuid) => !removedUuids.has(jointUuid));

  for (const joint of removedFreeJoints) {
    delete draft.joints[joint.uuid];
  }

  Object.entries(draft.actuators || {}).forEach(([actuatorUuid, actuator]) => {
    if (actuator?.joint && removedTargets.has(actuator.joint)) {
      delete draft.actuators?.[actuatorUuid];
    }
  });

  Object.entries(draft.equality || {}).forEach(([equalityUuid, equality]) => {
    const jointEquality = equality as
      | (Equality & { type?: string; name1?: string; name2?: string })
      | undefined;
    if (
      jointEquality?.type === "joint" &&
      ((jointEquality.name1 && removedTargets.has(jointEquality.name1)) ||
        (jointEquality.name2 && removedTargets.has(jointEquality.name2)))
    ) {
      delete draft.equality?.[equalityUuid];
    }
  });

  logger.info(
    "[bodyActions] Removed free joint from body after reparenting it under another body.",
    { bodyUuid, jointUuids: removedFreeJoints.map((joint) => joint.uuid) }
  );
};

export const createBodyHierarchyActions = (
  set: SceneStoreSetState,
  get: SceneStoreGetState
): Pick<BodyActions, "addBody" | "moveBodyToParent" | "getBodyParent"> => ({
  addBody: withHistory((bodyInput: BodyInput, parentUuid: BodyUUID | null = null): string => {
    const assignedUuid = (bodyInput.uuid ?? generateUuid()) as BodyUUID;
    const resolvedName = bodyInput.name; // Name is now part of bodyInput

    set((state) =>
      create(state, (draft) => {
        // Ensure unique name if provided, otherwise generate one
        let finalName = resolvedName;
        if (!finalName) {
          let counter = 1;
          while (
            draft.bodies &&
            Object.values(draft.bodies as BodyRecord).some((b) => b.name === `body_${counter}`)
          ) {
            counter++;
          }
          finalName = `body_${counter}`;
        } else {
          // Check for duplicates if name provided
          let counter = 1;
          let tempName = finalName;
          while (
            draft.bodies &&
            Object.values(draft.bodies as BodyRecord).some(
              (b) => b.name === tempName && b.uuid !== assignedUuid
            )
          ) {
            tempName = `${finalName}_${counter}`;
            counter++;
          }
          finalName = tempName;
        }

        const newBody: Body = {
          ...bodyInput,
          uuid: assignedUuid,
          name: finalName,
          children: bodyInput.children || [],
          sites: bodyInput.sites || [],
          geoms: bodyInput.geoms || [],
          joints: bodyInput.joints || [],
        } as Body;

        // Add directly to flat map
        draft.bodies[assignedUuid] = newBody;

        // Handle parent relationship
        if (parentUuid) {
          const parentBody = draft.bodies[parentUuid] || null;

          if (parentBody) {
            parentBody.children = parentBody.children || [];
            if (!parentBody.children.includes(assignedUuid)) {
              parentBody.children.push(assignedUuid);
            }
          } else {
            // Parent UUID not found - add to world bodies to avoid orphan
            logger.warn(`[addBody] Parent UUID "${parentUuid}" not found, adding to world bodies`);
            draft.worldBodies = draft.worldBodies || [];
            const existingIndex = draft.worldBodies.findIndex(
              (wb: WorldBodyRef) => (typeof wb === "string" ? wb : wb.uuid) === assignedUuid
            );
            if (existingIndex === -1) {
              // Always generate "Object 1", "Object 2", etc. for robot-level world body labels
              const worldBodyName = generateUniqueWorldBodyName(draft.worldBodies as WorldBody[]);
              draft.worldBodies.push({ uuid: assignedUuid, name: worldBodyName });
            }
          }
        } else {
          // No parent -> add to world bodies
          // Always generate "Object 1", "Object 2", etc. for robot-level world body labels
          draft.worldBodies = draft.worldBodies || [];
          const existingIndex = draft.worldBodies.findIndex(
            (wb: WorldBodyRef) => (typeof wb === "string" ? wb : wb.uuid) === assignedUuid
          );
          if (existingIndex === -1) {
            const worldBodyName = generateUniqueWorldBodyName(draft.worldBodies as WorldBody[]);
            draft.worldBodies.push({ uuid: assignedUuid, name: worldBodyName });
          }
        }
      })
    );

    return assignedUuid;
  }),

  // Move body to a new parent (or to root level) while preserving world-space pose
  // UUID-only
  moveBodyToParent: withHistory((bodyUuid: BodyUUID, newParentUuid: BodyUUID | null) => {
    set((state) =>
      create(state, (draft) => {
        const getObject = (uuid: string): THREE.Object3D | null => {
          try {
            const reg = (RuntimeRegistry.getAll() || {}) as RuntimeRegistryMap;
            if (reg[uuid]) return reg[uuid];
            // If not found, try to find by UUID in userData (for UUID-based registry)
            for (const key of Object.keys(reg)) {
              const obj = reg[key];
              if (obj && obj.userData && obj.userData.bodyUuid === uuid) {
                return obj;
              }
            }
            return null;
          } catch {
            return null;
          }
        };
        const worldMatrixOf = (uuid: string): THREE.Matrix4 | null => {
          const obj = getObject(uuid);
          if (!obj) return null;
          obj.updateMatrixWorld(true);
          return obj.matrixWorld.clone();
        };
        const findModelGroup = (): THREE.Object3D | null => {
          const reg = (RuntimeRegistry.getAll() || {}) as RuntimeRegistryMap;
          const keys = Object.keys(reg);
          if (keys.length === 0) return null;
          let obj: THREE.Object3D | undefined = reg[keys[0]];
          while (obj && obj.parent) {
            if (obj.name === "modelGroup") return obj;
            obj = obj.parent;
          }
          return null;
        };

        const actualBodyUuid = bodyUuid;
        const actualParentUuid = newParentUuid;

        // Verify both body and parent exist in the flat bodies map
        if (!state.bodies[actualBodyUuid]) {
          logger.error(`[moveBodyToParent] Body ${actualBodyUuid} not found in bodies map`);
          return;
        }
        if (actualParentUuid && !state.bodies[actualParentUuid]) {
          logger.error(
            `[moveBodyToParent] Parent body ${actualParentUuid} not found in bodies map. Available keys:`,
            Object.keys(state.bodies).slice(0, 10)
          );
          return;
        }

        // Check for circular reference: is the target parent a descendant of the body being moved?
        // If so, making the body a child of that parent would create a cycle
        const isDescendant = (
          ancestorUuid: string,
          potentialDescendantUuid: string,
          visited = new Set<string>()
        ): boolean => {
          if (visited.has(ancestorUuid)) return false; // Already visited, prevent infinite loop
          visited.add(ancestorUuid);

          const ancestor = state.bodies[ancestorUuid as BodyUUID];
          if (!ancestor || !Array.isArray(ancestor.children)) return false;

          for (const childUuid of ancestor.children) {
            if (childUuid === potentialDescendantUuid) return true;
            if (isDescendant(childUuid, potentialDescendantUuid, visited)) return true;
          }
          return false;
        };

        if (actualParentUuid && isDescendant(actualBodyUuid, actualParentUuid)) {
          const bodyName = state.bodies[actualBodyUuid]?.name || actualBodyUuid;
          const parentName = state.bodies[actualParentUuid]?.name || actualParentUuid;
          logger.error(
            `[moveBodyToParent] Cannot reparent: "${bodyName}" is an ancestor of "${parentName}". This would create a circular reference.`
          );
          return;
        }

        // Work directly with flat structure - no tree reconstruction needed
        const body = draft.bodies[actualBodyUuid];
        if (!body) {
          logger.error(`[moveBodyToParent] Body ${actualBodyUuid} not found`);
          return;
        }

        // Find current parent by searching all bodies for which one has this body as a child
        let currentParentUuid: string | null = null;
        for (const [uuid, b] of Object.entries(draft.bodies) as [string, Body][]) {
          if (b && Array.isArray(b.children) && b.children.includes(actualBodyUuid as BodyUUID)) {
            currentParentUuid = uuid;
            break;
          }
        }

        // Check if body is currently a root body (in worldBodies)
        const isCurrentlyRoot = draft.worldBodies.some((wb: WorldBodyRef) => {
          const wbUuid = typeof wb === "string" ? wb : wb.uuid;
          return wbUuid === actualBodyUuid;
        });

        // Calculate new local transform
        const childWorld = worldMatrixOf(actualBodyUuid) || new THREE.Matrix4();
        const parentObj = actualParentUuid ? getObject(actualParentUuid) : findModelGroup();
        const parentWorld = parentObj
          ? (parentObj.updateMatrixWorld(true), parentObj.matrixWorld.clone())
          : new THREE.Matrix4();
        const parentWorldInv = parentWorld.clone().invert();

        const localMat = parentWorldInv.multiply(childWorld);
        const p = new THREE.Vector3();
        const q = new THREE.Quaternion();
        const s = new THREE.Vector3();
        localMat.decompose(p, q, s);
        const newLocalPos: Vector3 = [p.x, p.y, p.z];
        const newLocalQuat: Quaternion = [q.w, q.x, q.y, q.z];

        // Remove from current parent
        if (currentParentUuid) {
          const currentParent = draft.bodies[currentParentUuid as BodyUUID];
          if (currentParent && Array.isArray(currentParent.children)) {
            const index = currentParent.children.indexOf(actualBodyUuid);
            if (index !== -1) {
              currentParent.children.splice(index, 1);
            }
          }
        } else if (isCurrentlyRoot) {
          // Remove from worldBodies
          draft.worldBodies = draft.worldBodies.filter((wb: WorldBodyRef) => {
            const wbUuid = typeof wb === "string" ? wb : wb.uuid;
            return wbUuid !== actualBodyUuid;
          });
        }

        // Add to new parent
        if (actualParentUuid) {
          removeBodyFreeJoints(draft, actualBodyUuid as BodyUUID);
          const newParent = draft.bodies[actualParentUuid];
          if (newParent) {
            if (!Array.isArray(newParent.children)) {
              newParent.children = [];
            }
            if (!newParent.children.includes(actualBodyUuid)) {
              newParent.children.push(actualBodyUuid);
            }
          }
        } else {
          // Moving to root - add to worldBodies
          const alreadyInWorldBodies = draft.worldBodies.some((wb: WorldBodyRef) => {
            const wbUuid = typeof wb === "string" ? wb : wb.uuid;
            return wbUuid === actualBodyUuid;
          });
          if (!alreadyInWorldBodies) {
            draft.worldBodies.push({
              uuid: actualBodyUuid as BodyUUID,
              name: generateUniqueWorldBodyName(draft.worldBodies),
            });
          }
        }

        // Update body's position and orientation
        body.position = newLocalPos;
        body.orientation = newLocalQuat;

        try {
          window.dispatchEvent(new CustomEvent("structure-changed"));
        } catch (error) {
          logger.warn(
            "[bodyActions] Failed to dispatch structure-changed after reparenting",
            error
          );
        }
      })
    );
  }),

  getBodyParent: (bodyUuid: BodyUUID): BodyUUID | null => {
    const state = get();
    const bodiesMap = state.bodies || {};

    // Direct flat map search - check which body has this as a child
    for (const body of Object.values(bodiesMap)) {
      if (!body || !Array.isArray(body.children)) continue;

      const hasChild = body.children.includes(bodyUuid);

      if (hasChild) {
        return body.uuid as BodyUUID;
      }
    }

    return null;
  },
});
